/**
 * Coregum, page d'accueil : comportements des sections cg-home-*.
 * - <cg-hero-slider>   : bannières défilantes (scroll-snap, flèches, tirets, autoplay)
 * - <cg-product-tabs>  : onglets de listes de produits
 * - <cg-carousel>      : rail horizontal de cartes avec flèches
 * - <cg-product-card>  : pastilles de saveur (image, variante, lien)
 * L'ajout au panier passe par <product-form-component> (product-form.js), qui ouvre le tiroir.
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isRtl = (el) => getComputedStyle(el).direction === 'rtl';

class CgHeroSlider extends HTMLElement {
  #abort = new AbortController();
  #timer = 0;
  #index = 0;

  connectedCallback() {
    const { signal } = this.#abort;
    this.track = this.querySelector('[data-cg-track]');
    this.slides = [...this.querySelectorAll('[data-cg-slide]')];
    this.dots = [...this.querySelectorAll('[data-cg-dot]')];
    if (!this.track || this.slides.length < 2) return;

    this.querySelector('[data-cg-prev]')?.addEventListener('click', () => this.go(this.#index - 1), { signal });
    this.querySelector('[data-cg-next]')?.addEventListener('click', () => this.go(this.#index + 1), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i), { signal }));

    let raf = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.#sync());
      },
      { signal, passive: true }
    );

    const delay = Number(this.dataset.autoplay || 0) * 1000;
    if (delay > 0 && !reducedMotion()) {
      const start = () => {
        this.#stop();
        this.#timer = window.setInterval(() => this.go(this.#index + 1), delay);
      };
      ['pointerenter', 'focusin', 'touchstart'].forEach((type) =>
        this.addEventListener(type, () => this.#stop(), { signal, passive: true })
      );
      ['pointerleave', 'focusout'].forEach((type) => this.addEventListener(type, start, { signal }));
      document.addEventListener('visibilitychange', () => (document.hidden ? this.#stop() : start()), { signal });
      start();
    }
    this.#sync();
  }

  disconnectedCallback() {
    this.#stop();
    this.#abort.abort();
  }

  #stop() {
    if (this.#timer) window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  /** @param {number} i */
  go(i) {
    const count = this.slides.length;
    const index = (i + count) % count;
    const width = this.track.clientWidth;
    this.track.scrollTo({
      left: index * width * (isRtl(this) ? -1 : 1),
      behavior: reducedMotion() ? 'auto' : 'smooth',
    });
  }

  #sync() {
    const width = this.track.clientWidth || 1;
    const index = Math.round(Math.abs(this.track.scrollLeft) / width);
    this.#index = Math.min(Math.max(index, 0), this.slides.length - 1);
    this.slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.#index)));
  }
}

class CgCarousel extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.track = this.querySelector('[data-cg-track]');
    this.prev = this.querySelector('[data-cg-prev]');
    this.next = this.querySelector('[data-cg-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.step(-1), { signal });
    this.next?.addEventListener('click', () => this.step(1), { signal });
    this.track.addEventListener('scroll', () => this.update(), { signal, passive: true });
    const observer = new ResizeObserver(() => this.update());
    observer.observe(this.track);
    signal.addEventListener('abort', () => observer.disconnect());
    this.update();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {number} direction */
  step(direction) {
    const card = this.track.querySelector('[data-cg-item]');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const amount = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    const sign = isRtl(this) ? -1 : 1;
    this.track.scrollBy({ left: amount * direction * sign, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  update() {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const pos = Math.abs(this.track.scrollLeft);
    this.toggleAttribute('data-overflowing', max > 4);
    if (this.prev) this.prev.disabled = pos <= 4;
    if (this.next) this.next.disabled = pos >= max - 4;
  }

  reset() {
    this.track?.scrollTo({ left: 0 });
    this.update();
  }
}

class CgProductTabs extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i), { signal });
      tab.addEventListener(
        'keydown',
        (event) => {
          const keys = { ArrowRight: 1, ArrowLeft: -1 };
          if (!(event.key in keys)) return;
          event.preventDefault();
          const sign = isRtl(this) ? -1 : 1;
          const next = (i + keys[event.key] * sign + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        },
        { signal }
      );
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {number} index */
  select(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls') || '');
      if (!panel) return;
      panel.hidden = !active;
      if (active) panel.querySelector('cg-carousel')?.reset?.();
    });
  }
}

class CgProductCard extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.querySelectorAll('[data-cg-swatch]').forEach((swatch) =>
      swatch.addEventListener('click', () => this.select(/** @type {HTMLElement} */ (swatch)), { signal })
    );
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {HTMLElement} swatch */
  select(swatch) {
    this.querySelectorAll('[data-cg-swatch]').forEach((el) => {
      const active = el === swatch;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-checked', String(active));
    });

    const { variantId, image, srcset, url, available, price } = swatch.dataset;
    const img = this.querySelector('[data-cg-card-image]');
    if (img instanceof HTMLImageElement && image) {
      img.srcset = srcset || '';
      img.src = image;
    }
    this.querySelectorAll('[data-cg-card-link]').forEach((link) => {
      if (link instanceof HTMLAnchorElement && url) link.href = url;
    });
    const input = this.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement && variantId) input.value = variantId;
    const button = this.querySelector('[data-cg-add]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = available === 'false';
      const label = button.querySelector('[data-cg-add-label]');
      if (label) label.textContent = available === 'false' ? button.dataset.soldOut || '' : button.dataset.add || '';
      const priceEl = button.querySelector('[data-cg-price]');
      if (priceEl && price) priceEl.textContent = price;
      button.querySelectorAll('[data-cg-price-part]').forEach((el) => {
        if (el instanceof HTMLElement) el.hidden = available === 'false';
      });
    }
  }
}

if (!customElements.get('cg-hero-slider')) customElements.define('cg-hero-slider', CgHeroSlider);
if (!customElements.get('cg-carousel')) customElements.define('cg-carousel', CgCarousel);
if (!customElements.get('cg-product-tabs')) customElements.define('cg-product-tabs', CgProductTabs);
if (!customElements.get('cg-product-card')) customElements.define('cg-product-card', CgProductCard);
